import React from 'react'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'

import { RootStateTypeExtra } from '@src/store/types'

import { selectFullscreen } from '@store/actionSlices/appConfig'

import { LotInterface } from '@api/types/house-and-land-type'

import {
  CloseSvg,
  CompareSvg,
  EyeSlashSvg,
  EyeSvg,
  FullScreenSvg,
  PanoramaSvg,
  PlaySvg,
  StopCompareSvg,
  StopSvg,
} from '@svg/react'

import { useCompareProperty } from '@adUtilities/components/compare-property/utils'
import { PropertyType } from '@adUtilities/types'

interface ComponentProps {
  lot: LotInterface
  hasPanoramicData: boolean
  setFullScreenToggle: (arg: boolean) => void
  packageInfoToggle: boolean
  setPackageInfoToggle: (arg: boolean) => void
  slideShowToggle: boolean
  setSlideShowToggle: (arg: boolean) => void
  isVideoPlaying: boolean
  shouldShowSlideshowBtn: boolean
  comparePropertyProps: ReturnType<typeof useCompareProperty>
  hideCompareAction?: boolean
  panoramicButtonTitle: string
}

function Controls({
  lot,
  hasPanoramicData,
  setFullScreenToggle,
  packageInfoToggle,
  setPackageInfoToggle,
  slideShowToggle,
  setSlideShowToggle,
  isVideoPlaying,
  shouldShowSlideshowBtn,
  comparePropertyProps,
  hideCompareAction,
  panoramicButtonTitle,
}: ComponentProps) {
  const fullscreen = useSelector(selectFullscreen)
  const activePrecinctId = useSelector(
    (state: RootStateTypeExtra) => state.houseAndLand.activePrecinctId
  )
  const activeStageId = useSelector(
    (state: RootStateTypeExtra) => state.houseAndLand.activeStageId
  )

  const {
    checkPropertyComparability,
    compareModeIsActive,
    startComparison,
    stopComparison,
  } = comparePropertyProps

  const isComparable = React.useMemo<boolean>(
    () =>
      !hideCompareAction &&
      checkPropertyComparability({
        propertyType: PropertyType.Lot,
        data: lot,
      }),
    [lot, checkPropertyComparability, hideCompareAction]
  )

  const handleToggleCompareMode = React.useCallback(() => {
    if (!compareModeIsActive) {
      startComparison({
        property: {
          propertyType: PropertyType.Lot,
          data: lot,
          references: {
            precinctId: activePrecinctId,
            stageId: activeStageId,
          },
        },
      })
      return
    }
    stopComparison()
  }, [
    compareModeIsActive,
    startComparison,
    stopComparison,
    lot,
    activePrecinctId,
    activeStageId,
  ])

  return (
    <div
      className={`flex flex-col items-end space-y-4 ${
        isVideoPlaying && 'hidden'
      }`}
    >
      {!slideShowToggle && (
        <>
          <button
            type="button"
            onClick={() => setFullScreenToggle(!fullscreen)}
            className="flex items-center space-x-2"
          >
            <span
              className={`lot-view-control-text ${
                !packageInfoToggle ? 'opacity-0' : 'opacity-100'
              }`}
            >
              {fullscreen && 'Exit'} Fullscreen
            </span>
            <span className="lot-view-control-icon">
              {fullscreen ? (
                <CloseSvg className="h-5 w-5" />
              ) : (
                <FullScreenSvg className="h-5 w-5" />
              )}
            </span>
          </button>
          <button
            type="button"
            onClick={() => setPackageInfoToggle(!packageInfoToggle)}
            className="flex items-center space-x-2"
          >
            <span
              className={`lot-view-control-text ${
                !packageInfoToggle ? 'opacity-0' : 'opacity-100'
              }`}
            >
              {packageInfoToggle ? 'Hide' : 'Show'} Info
            </span>
            <span className="lot-view-control-icon">
              {packageInfoToggle ? (
                <EyeSlashSvg className="h-5 w-5" />
              ) : (
                <EyeSvg className="h-5 w-5" />
              )}
            </span>
          </button>
          {isComparable && !slideShowToggle ? (
            <button
              type="button"
              onClick={handleToggleCompareMode}
              className="flex items-center space-x-2"
            >
              <span
                className={`lot-view-control-text ${
                  !packageInfoToggle ? 'opacity-0' : 'opacity-100'
                }`}
              >
                {compareModeIsActive ? 'Stop Comparing' : 'Compare'}
              </span>
              <span className="lot-view-control-icon">
                {compareModeIsActive ? (
                  <StopCompareSvg
                    className="h-6 w-6 fill-white stroke-white"
                    stroke={1.6}
                  />
                ) : (
                  <CompareSvg className="h-6 w-6 stroke-white" stroke={1.6} />
                )}
              </span>
            </button>
          ) : null}
          {hasPanoramicData && !compareModeIsActive && (
            <Link to="panoramic" className="flex items-center space-x-2">
              <span
                className={`lot-view-control-text ${
                  !packageInfoToggle ? 'opacity-0' : 'opacity-100'
                }`}
              >
                {panoramicButtonTitle}
              </span>
              <span className="lot-view-control-icon">
                <PanoramaSvg className="h-5 w-5" />
              </span>
            </Link>
          )}
        </>
      )}
      {shouldShowSlideshowBtn && !compareModeIsActive && (
        <button
          type="button"
          onClick={() => setSlideShowToggle(!slideShowToggle)}
          className="flex items-center space-x-2"
        >
          <span
            className={`lot-view-control-text ${
              !packageInfoToggle ? 'opacity-0' : 'opacity-100'
            }`}
          >
            {!slideShowToggle ? 'Start' : 'Stop'} Slideshow
          </span>
          <span className="lot-view-control-icon">
            {!slideShowToggle ? (
              <PlaySvg className="h-3 w-3" />
            ) : (
              <StopSvg className="h-4 w-4" />
            )}
          </span>
        </button>
      )}
    </div>
  )
}

export default Controls
